import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
	Alert,
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	CircularProgress,
	Container,
	Grid,
	IconButton,
	Paper,
	Stack,
	Tab,
	Tabs,
	Tooltip,
	Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import LoginIcon from '@mui/icons-material/Login';
import LocalCafeIcon from '@mui/icons-material/LocalCafe';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { Link as RouterLink } from 'react-router-dom';
import NuvvyCharacter from '../../../components/NuvvyCharacter/NuvvyCharacter';
import { getClientMenus } from '../../../services/Clint/UserOrdersApi';
import { resolveImageUrl } from '../../../services/indexMediaBaseUrl';
import logo from '../../../Assets/logo-removebg.png';

const UNCATEGORIZED = 'بدون تصنيف';
const brandGreen = '#173525';
const brandCream = '#fffff1';
const brandLight = '#fcffde';

const assistantMessages = [
	'NUVVY مش مجرد كافيه، دي تجربة Smart Cafe بتخليك تختار وتطلب بطريقة أسهل.',
	'سجل حسابك مرة واحدة، وبعدها تقدر تطلب من موبايلك وتتابع طلباتك براحتك.',
	'مع NUVVY تقدر تصنع مشروب خاص بيك بالذكاء الاصطناعي، تجربة جديدة في مصر والشرق الأوسط.',
];

const toPrice = (value) => {
	const amount = Number(value || 0);
	return Number.isNaN(amount) ? '0.00' : amount.toFixed(2);
};

const groupItemsByCategory = (items = []) =>
	items.reduce((acc, item) => {
		const key = item?.category?.name || UNCATEGORIZED;
		if (!acc[key]) acc[key] = [];
		acc[key].push(item);
		return acc;
	}, {});

const isVisibleItem = (item) => Number(item?.status) !== 0;
const isPublicApprovedDrink = (drink) =>
	Number(drink?.is_approved) === 1 &&
	Number(drink?.is_public) === 1 &&
	Number(drink?.status) === 1;

const MenuCard = ({ item, type }) => {
	const isDrink = type === 'drink';
	const title = isDrink ? item.drink_name || item.name || 'مشروب' : item.name || 'منتج';
	const imageUrl = item?.image ? resolveImageUrl(item.image) : '';

	return (
		<Paper
			elevation={0}
			sx={{
				border: '1px solid rgba(47, 87, 65, 0.12)',
				borderRadius: '8px',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'space-between',
				gap: 1.5,
				p: { xs: 1.35, sm: 1.5 },
				backgroundColor: '#ffffff',
				transition: '0.2s ease',
				'&:hover': {
					transform: 'translateX(-3px)',
					boxShadow: '0 12px 28px rgba(23, 53, 37, 0.1)',
					borderColor: isDrink ? '#d6aa38' : brandGreen,
				},
			}}
		>
			<Box sx={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 1.15 }}>
				{imageUrl ? (
					<Box
						component="img"
						src={imageUrl}
						alt={title}
						sx={{
							width: { xs: 54, sm: 62 },
							height: { xs: 54, sm: 62 },
							flexShrink: 0,
							borderRadius: '8px',
							objectFit: 'cover',
							border: '1px solid rgba(47, 87, 65, 0.12)',
							backgroundColor: 'rgba(252, 255, 222, 0.42)',
						}}
					/>
				) : null}
				<Typography
					variant="body1"
					sx={{
						minWidth: 0,
						fontWeight: 900,
						color: brandGreen,
						lineHeight: 1.5,
						overflowWrap: 'anywhere',
					}}
				>
					{title}
				</Typography>
			</Box>
			<Chip
				label={`${toPrice(item.price)} ج.م`}
				sx={{
					flexShrink: 0,
					fontWeight: 900,
					color: brandGreen,
					backgroundColor: 'rgba(252, 255, 222, 0.88)',
					border: '1px solid rgba(47, 87, 65, 0.12)',
				}}
			/>
		</Paper>
	);
};

const CategoryCard = ({ categoryName, items, color = 'primary', icon, onClick }) => {
	return (
		<Card
			elevation={0}
			onClick={onClick}
			sx={{
				height: '100%',
				cursor: 'pointer',
				border: '1px solid rgba(47, 87, 65, 0.12)',
				borderRadius: '8px',
				overflow: 'hidden',
				backgroundColor: '#ffffff',
				transition: '0.2s ease',
				'&:hover': {
					transform: 'translateY(-3px)',
					boxShadow: '0 16px 34px rgba(23, 53, 37, 0.12)',
					borderColor: color === 'primary' ? brandGreen : '#d6aa38',
				},
			}}
		>
			<CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, minHeight: 132 }}>
				<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
					<Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1.35, color: brandGreen }} noWrap>
						{categoryName}
					</Typography>
					<Box sx={{ color: color === 'primary' ? brandGreen : '#d6aa38', display: 'flex', alignItems: 'center' }}>
						{icon}
					</Box>
				</Box>

				<Chip
					size="small"
					color={color}
					variant="outlined"
					label={`${items.length} عنصر`}
					sx={{ alignSelf: 'flex-start', fontWeight: 900, borderColor: 'rgba(47, 87, 65, 0.18)' }}
				/>
			</CardContent>
		</Card>
	);
};

const CategoryHeader = ({ title, color = 'primary', onBack }) => (
	<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, mb: 2 }}>
		<Button
			variant="outlined"
			color={color}
			startIcon={<ArrowBackIcon />}
			onClick={onBack}
			sx={{ borderRadius: '8px', fontWeight: 900, borderColor: 'rgba(47, 87, 65, 0.22)', color: brandGreen }}
		>
			التصنيفات
		</Button>
		<Typography variant="h6" sx={{ fontWeight: 900, color: brandGreen }} noWrap>
			{title}
		</Typography>
	</Box>
);

const Home = () => {
	const [products, setProducts] = useState([]);
	const [drinks, setDrinks] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState('');
	const [activeTab, setActiveTab] = useState(0);
	const [selectedProductCategory, setSelectedProductCategory] = useState('');
	const [assistantMessageIndex, setAssistantMessageIndex] = useState(0);
	const [assistantPosition, setAssistantPosition] = useState(null);
	const [isAssistantDragging, setIsAssistantDragging] = useState(false);
	const assistantRef = useRef(null);
	const assistantDragOffsetRef = useRef({ x: 0, y: 0 });

	const groupedProducts = useMemo(() => groupItemsByCategory(products), [products]);

	const fetchMenus = async () => {
		setLoading(true);
		setError('');
		try {
			const response = await getClientMenus();
			setProducts((response?.data?.products || []).filter(isVisibleItem));
			setDrinks((response?.data?.drinks || []).filter(isPublicApprovedDrink));
		} catch (err) {
			setError(err?.message || 'حدث خطأ أثناء جلب المنيو');
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		document.title = 'Nuvvy - المنيو';
		fetchMenus();
	}, []);

	useEffect(() => {
		const interval = window.setInterval(() => {
			setAssistantMessageIndex((current) => (current + 1) % assistantMessages.length);
		}, 5200);

		return () => window.clearInterval(interval);
	}, []);

	const handleAssistantPointerDown = (event) => {
		const rect = assistantRef.current?.getBoundingClientRect();
		if (!rect) return;

		assistantDragOffsetRef.current = {
			x: event.clientX - rect.left,
			y: event.clientY - rect.top,
		};
		setAssistantPosition({ left: rect.left, top: rect.top });
		setIsAssistantDragging(true);
		event.currentTarget.setPointerCapture?.(event.pointerId);
	};

	const handleAssistantPointerMove = (event) => {
		if (!isAssistantDragging) return;

		const rect = assistantRef.current?.getBoundingClientRect();
		if (!rect) return;

		const maxLeft = Math.max(6, window.innerWidth - rect.width - 6);
		const maxTop = Math.max(6, window.innerHeight - rect.height - 6);
		const nextLeft = Math.min(Math.max(6, event.clientX - assistantDragOffsetRef.current.x), maxLeft);
		const nextTop = Math.min(Math.max(6, event.clientY - assistantDragOffsetRef.current.y), maxTop);

		setAssistantPosition({ left: nextLeft, top: nextTop });
	};

	const handleAssistantPointerUp = (event) => {
		setIsAssistantDragging(false);
		event.currentTarget.releasePointerCapture?.(event.pointerId);
	};

	const renderCategoryGrid = (entries) => {
		return (
			<Grid container spacing={2} sx={{ mt: 2 }}>
				{entries.map(([categoryName, items]) => (
					<Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={`product-category-${categoryName}`}>
						<CategoryCard
							categoryName={categoryName}
							items={items}
							color="primary"
							icon={<LocalCafeIcon />}
							onClick={() => setSelectedProductCategory(categoryName)}
						/>
					</Grid>
				))}
			</Grid>
		);
	};

	const renderItemsList = (items, type) => (
		<Stack spacing={1.1}>
			{items.map((item) => (
				<Box key={`${type}-${item.id}`}>
					<MenuCard item={item} type={type} />
				</Box>
			))}
		</Stack>
	);

	return (
		<Box
			dir="rtl"
			lang="ar"
			sx={{
				minHeight: '100vh',
				background: `linear-gradient(180deg, ${brandCream} 0%, #f8faec 46%, #ffffff 100%)`,
				position: 'relative',
				overflowX: 'hidden',
			}}
		>
			<Box
				component="section"
				sx={{
					backgroundColor: brandGreen,
					color: brandCream,
					borderBottom: '1px solid rgba(47, 87, 65, 0.12)',
					py: { xs: 4, md: 6 },
				}}
			>
				<Container maxWidth="lg">
					<Grid container spacing={{ xs: 3, md: 5 }} alignItems="center">
						<Grid size={{ xs: 12, md: 7 }}>
							<Stack spacing={2.2}>
								<Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', gap: 1 }}>
									<Chip
										icon={<AutoAwesomeIcon />}
										label="أول تجربة AI Cafe في مصر والشرق الأوسط"
										sx={{
											color: brandGreen,
											backgroundColor: brandLight,
											fontWeight: 900,
											'& .MuiChip-icon': { color: '#bd8d2b' },
										}}
									/>
									<Chip
										icon={<PhoneIphoneIcon />}
										label="اطلب من الموبايل"
										sx={{
											color: brandCream,
											backgroundColor: 'rgba(255, 255, 241, 0.1)',
											border: '1px solid rgba(252, 255, 222, 0.24)',
											fontWeight: 800,
											'& .MuiChip-icon': { color: '#f7efbe' },
										}}
									/>
								</Stack>

								<Typography
									component="h1"
									sx={{
										fontSize: { xs: 34, sm: 44, md: 58 },
										fontWeight: 950,
										lineHeight: 1.18,
										letterSpacing: 0,
									}}
								>
									NUVVY
								</Typography>
								<Typography
									sx={{
										fontSize: { xs: 17, md: 21 },
										lineHeight: 1.9,
										color: 'rgba(255, 255, 241, 0.84)',
										maxWidth: 760,
									}}
								>
									كافيه ذكي يقدم تجربة مختلفة: شوف المنيو، زور الفرع , جرب تعمل اوردر علي حسب مودك , وبعدها تقدر تطلب من موبايلك بسهولة.
									ولو حابب حاجة على مزاجك، NUVVY يساعدك تعمل مشروب خاص بيك باستخدام AI.
								</Typography>

								<Stack direction="row" spacing={1.25} alignItems="center">
									<Tooltip title="تسجيل الدخول">
										<IconButton
											component={RouterLink}
											to="/login"
											aria-label="تسجيل الدخول"
											sx={{
												width: 54,
												height: 54,
												color: brandGreen,
												background: `linear-gradient(145deg, ${brandLight} 0%, #f7efbe 100%)`,
												border: '1px solid rgba(252, 255, 222, 0.5)',
												boxShadow: '0 16px 34px rgba(0, 0, 0, 0.18)',
												'&:hover': {
													background: `linear-gradient(145deg, #ffffff 0%, ${brandLight} 100%)`,
													transform: 'translateY(-2px)',
												},
											}}
										>
											<LoginIcon />
										</IconButton>
									</Tooltip>
									<Typography sx={{ color: 'rgba(255, 255, 241, 0.72)', fontWeight: 800 }}>
										سجل دخولك وابدأ تجربة الطلب الذكية
									</Typography>
								</Stack>
							</Stack>
						</Grid>

						<Grid size={{ xs: 12, md: 5 }}>
							<Box
								sx={{
									display: 'flex',
									justifyContent: { xs: 'center', md: 'flex-end' },
								}}
							>
								<Box
									sx={{
										width: { xs: 210, sm: 260, md: 330 },
										minHeight: { xs: 170, sm: 210, md: 260 },
										display: 'grid',
										placeItems: 'center',
										p: { xs: 1, md: 2 },
									}}
								>
									<Box
										component="img"
										src={logo}
										alt="Nuvvy"
										sx={{
											width: '100%',
											maxHeight: { xs: 170, sm: 210, md: 260 },
											objectFit: 'contain',
											filter: 'drop-shadow(0 26px 44px rgba(0, 0, 0, 0.24))',
										}}
									/>
								</Box>
							</Box>
						</Grid>
					</Grid>
				</Container>
			</Box>

			<Container maxWidth="lg" sx={{ py: { xs: 3, md: 4.5 }, pb: { xs: 18, md: 5 } }}>
				<Stack spacing={1} sx={{ mb: 2.5 }}>
					<Stack direction="row" spacing={1} alignItems="center">
						<SmartToyIcon sx={{ color: brandGreen }} />
						<Typography variant="h5" sx={{ fontWeight: 950, color: brandGreen }}>
							استكشف منيو NUVVY
						</Typography>
					</Stack>
					<Typography color="text.secondary" sx={{ lineHeight: 1.8 }}>
						اختار التصنيف الأول، وبعدها شوف الأصناف اللي جواه. ومشروبات العملاء المعتمدة تظهر هنا بدون تصنيفات.
					</Typography>
				</Stack>

				{error ? (
					<Alert severity="error" sx={{ mb: 2 }}>
						{error}
					</Alert>
				) : null}

				<Paper
					elevation={0}
					sx={{
						borderRadius: '8px',
						border: '1px solid rgba(47, 87, 65, 0.12)',
						backgroundColor: '#ffffff',
						overflow: 'hidden',
						boxShadow: '0 14px 36px rgba(23, 53, 37, 0.07)',
					}}
				>
					<Tabs
						value={activeTab}
						onChange={(_, value) => setActiveTab(value)}
						variant="fullWidth"
						sx={{
							'& .MuiTab-root': {
								minHeight: 58,
								fontWeight: 900,
							},
							'& .Mui-selected': {
								color: `${brandGreen} !important`,
							},
							'& .MuiTabs-indicator': {
								backgroundColor: brandGreen,
								height: 3,
							},
						}}
					>
						<Tab icon={<LocalCafeIcon />} iconPosition="start" label={`منيو (${products.length})`} />
						<Tab icon={<LocalCafeIcon />} iconPosition="start" label={`تم صنعها بواسطة العملاء (${drinks.length})`} />
					</Tabs>
				</Paper>

				{loading ? (
					<Box sx={{ minHeight: 260, display: 'grid', placeItems: 'center' }}>
						<CircularProgress />
					</Box>
				) : null}

				{!loading && activeTab === 0 ? (
					Object.keys(groupedProducts).length === 0 ? (
						<Alert severity="info" sx={{ mt: 2 }}>
							لا توجد أصناف حاليًا.
						</Alert>
					) : !selectedProductCategory ? (
						renderCategoryGrid(Object.entries(groupedProducts))
					) : (
						<Box sx={{ mt: 2 }}>
							<CategoryHeader
								title={selectedProductCategory}
								color="primary"
								onBack={() => setSelectedProductCategory('')}
							/>
							{renderItemsList(groupedProducts[selectedProductCategory] || [], 'product')}
						</Box>
					)
				) : null}

				{!loading && activeTab === 1 ? (
					drinks.length === 0 ? (
						<Alert severity="info" sx={{ mt: 2 }}>
							لا توجد مشروبات حاليًا.
						</Alert>
					) : (
						<Box sx={{ mt: 2 }}>
							{renderItemsList(drinks, 'drink')}
						</Box>
					)
				) : null}
			</Container>

			<Box
				ref={assistantRef}
				onPointerDown={handleAssistantPointerDown}
				onPointerMove={handleAssistantPointerMove}
				onPointerUp={handleAssistantPointerUp}
				onPointerCancel={handleAssistantPointerUp}
				sx={{
					position: 'fixed',
					left: assistantPosition ? `${assistantPosition.left}px` : { xs: 6, sm: 14, md: 20 },
					top: assistantPosition ? `${assistantPosition.top}px` : 'auto',
					bottom: assistantPosition ? 'auto' : { xs: 8, sm: 12, md: 18 },
					zIndex: 1100,
					cursor: isAssistantDragging ? 'grabbing' : 'grab',
					touchAction: 'none',
					userSelect: 'none',
					transform: { xs: 'scale(0.62)', sm: 'scale(0.78)', md: 'scale(0.92)' },
					transformOrigin: assistantPosition ? 'top left' : 'bottom left',
					transition: isAssistantDragging ? 'none' : 'filter 0.2s ease, transform 0.2s ease',
					filter: isAssistantDragging ? 'drop-shadow(0 18px 32px rgba(23, 53, 37, 0.16))' : 'none',
				}}
			>
				<NuvvyCharacter
					compact
					size="small"
					state={assistantMessageIndex === 2 ? 'happy' : 'talking'}
					showBubble
					message={assistantMessages[assistantMessageIndex]}
				/>
			</Box>
		</Box>
	);
};

export default Home;
